import { useCallback, useEffect, useMemo, useState } from "react";
import { lumiFetch } from "./lumiApi";
import { toast } from "./lumiToast";

// Lumi admin view of every hotel account — reuses the table + search + filter
// design pattern from the family Purchases tab. Data comes from the hotel
// admin endpoints in hotel_routes.py (mounted under /api/lumi/...).
//
// UI: master list on top with search+filter, click any row to expand full
// record (codes, devices, payments, ledger) beneath the row.

const TIER_LABELS = { boutique: "Boutique", resort: "Resort", group: "Group" };

const STATUS_STYLE = {
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  paused: "bg-amber-100 text-amber-800 border-amber-200",
  cancelled: "bg-neutral-200 text-neutral-700 border-neutral-300",
  expired: "bg-neutral-200 text-neutral-700 border-neutral-300",
};

export default function HotelPurchasesTab() {
  const [hotels, setHotels] = useState([]);
  const [q, setQ] = useState("");
  const [tier, setTier] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const qs = new URLSearchParams();
      if (q) qs.set("q", q);
      if (tier) qs.set("tier", tier);
      if (status) qs.set("status", status);
      const r = await lumiFetch(`/hotels${qs.toString() ? `?${qs.toString()}` : ""}`);
      setHotels(r.hotels || []);
    } catch (e) {
      setError(e?.data?.detail || "Could not load hotels");
    } finally {
      setLoading(false);
    }
  }, [q, tier, status]);

  useEffect(() => { load(); }, [load]);

  const openHotel = async (h) => {
    if (openId === h.hotel_id) {
      setOpenId(null);
      setDetail(null);
      return;
    }
    setOpenId(h.hotel_id);
    setDetailLoading(true);
    setDetail(null);
    try {
      const r = await lumiFetch(`/hotels/${h.hotel_id}`);
      setDetail(r);
    } catch (e) {
      toast.error(e?.data?.detail || "Could not load hotel");
    } finally {
      setDetailLoading(false);
    }
  };

  const refresh = () => { load(); if (openId) openHotel({ hotel_id: openId }); };

  const totals = useMemo(() => {
    const t = { active: 0, paused: 0, cancelled: 0, total_credits: 0 };
    for (const h of hotels) {
      t[h.status] = (t[h.status] || 0) + 1;
      t.total_credits += (h.monthly_credits_remaining || 0) + (h.addon_credits_remaining || 0);
    }
    return t;
  }, [hotels]);

  return (
    <div className="space-y-4" data-testid="lumi-hotel-purchases-tab">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <h2 className="text-xl font-bold">Hotel Purchases</h2>
        <div className="text-xs text-neutral-500">
          {hotels.length} hotels · {totals.active} active · {totals.paused} paused · {totals.cancelled} cancelled · {totals.total_credits.toLocaleString()} credits in pool
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search hotel, address, manager email, code…"
          data-testid="lumi-hotel-search"
          className="px-3 py-2 rounded-lg border border-neutral-300 text-sm min-w-[260px] flex-1"
        />
        <select value={tier} onChange={(e) => setTier(e.target.value)} data-testid="lumi-hotel-filter-tier" className="px-3 py-2 rounded-lg border border-neutral-300 text-sm">
          <option value="">All tiers</option>
          <option value="boutique">Boutique</option>
          <option value="resort">Resort</option>
          <option value="group">Group</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="lumi-hotel-filter-status" className="px-3 py-2 rounded-lg border border-neutral-300 text-sm">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="cancelled">Cancelled</option>
          <option value="expired">Expired</option>
        </select>
      </div>

      {error && <div className="bg-rose-50 border border-rose-200 text-rose-800 rounded-lg p-3 text-sm">{error}</div>}
      {loading && <div className="text-sm text-neutral-500">Loading…</div>}

      {!loading && hotels.length === 0 && (
        <div className="text-sm text-neutral-500 border border-dashed border-neutral-300 rounded-xl p-8 text-center">
          {q || tier || status ? "No hotels match those filters." : "No hotel purchases yet."}
        </div>
      )}

      <div className="space-y-2">
        {hotels.map((h) => (
          <div key={h.hotel_id} className="border border-neutral-200 rounded-xl bg-white">
            <button
              onClick={() => openHotel(h)}
              data-testid={`lumi-hotel-row-${h.hotel_id}`}
              className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-neutral-50"
            >
              <div className="min-w-0 pr-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-neutral-900 truncate">{h.hotel_name}</p>
                  <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border ${STATUS_STYLE[h.status] || STATUS_STYLE.cancelled}`}>{h.status || "cancelled"}</span>
                  <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-100">{TIER_LABELS[h.membership_tier_key] || h.membership_tier_name}</span>
                </div>
                <p className="text-xs text-neutral-600 truncate">{h.hotel_address} · {h.country} · {h.manager_email}</p>
                <p className="text-[11px] text-neutral-500 truncate mt-1">
                  {h.codes_count} code(s) · {(h.monthly_credits_remaining ?? 0).toLocaleString()} monthly + {(h.addon_credits_remaining ?? 0).toLocaleString()} add-on = {((h.monthly_credits_remaining ?? 0) + (h.addon_credits_remaining ?? 0)).toLocaleString()} credits available · Expected devices: {(h.venue_device_counts || []).reduce((a, b) => a + (b || 0), 0) || "—"} · Renews {h.next_renewal_at ? new Date(h.next_renewal_at).toLocaleDateString() : "—"}
                </p>
              </div>
              <div className="text-xs text-neutral-400 whitespace-nowrap">
                {new Date(h.created_at).toLocaleDateString()}
              </div>
            </button>

            {openId === h.hotel_id && (
              <div className="border-t border-neutral-200 bg-neutral-50 px-4 py-4">
                {detailLoading && <p className="text-xs text-neutral-500">Loading hotel details…</p>}
                {detail && <HotelDetail data={detail} onRefresh={refresh} />}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function HotelDetail({ data, onRefresh }) {
  const { hotel, codes, devices, payments, ledger, device_limit_audits: audits } = data;
  const [busy, setBusy] = useState("");

  const doAction = async (label, path, method = "POST", body) => {
    setBusy(label);
    try {
      await lumiFetch(path, { method, body });
      toast.success(`${label} done`);
      onRefresh();
    } catch (e) {
      toast.error(e?.data?.detail || `${label} failed`);
    } finally {
      setBusy("");
    }
  };

  const copyCode = (code) => {
    try {
      navigator.clipboard.writeText(code);
      toast.success(`Copied ${code}`);
    } catch {
      toast.error("Copy failed");
    }
  };

  const renameCode = async (code_id) => {
    const label = prompt("New venue label:");
    if (!label) return;
    await doAction("Rename", `/codes/${code_id}/label`, "POST", { venue_label: label });
  };

  const editDeviceLimit = async (code) => {
    const current = code.expected_devices || 1;
    const val = prompt(`Maximum active devices for "${code.venue_label}" (currently ${current}):`, String(current));
    if (!val) return;
    const n = parseInt(val, 10);
    if (!Number.isFinite(n) || n < 1 || n > 200) {
      toast.error("Please enter a whole number between 1 and 200.");
      return;
    }
    await doAction("Update device limit", `/codes/${code.code_id}/device-limit`, "POST", { expected_devices: n });
  };

  const editContact = async () => {
    const patch = {};
    const fields = [
      ["hotel_name", "Hotel name"],
      ["hotel_address", "Hotel address"],
      ["country", "Country"],
      ["manager_name", "Manager name"],
      ["manager_email", "Manager email"],
      ["billing_email", "Billing email"],
    ];
    for (const [k, label] of fields) {
      const v = prompt(`${label} (leave blank to keep):`, hotel[k] || "");
      if (v && v.trim() && v.trim() !== hotel[k]) patch[k] = v.trim();
    }
    if (Object.keys(patch).length === 0) return;
    await doAction("Edit contact", `/hotels/${hotel.hotel_id}/edit-contact`, "POST", patch);
  };

  const editWelcomeName = async () => {
    const v = prompt("Welcome name shown to children and families:", hotel.welcome_name || hotel.hotel_name || "");
    if (v == null) return;
    const val = v.trim();
    if (!val) return;
    await doAction("Update welcome name", `/hotels/${hotel.hotel_id}/welcome-name`, "POST", { welcome_name: val });
  };

  return (
    <div className="space-y-4 text-sm">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Info label="Welcome name (child-facing)" value={hotel.welcome_name} highlight />
        <Info label="Manager" value={hotel.manager_name} />
        <Info label="Manager email" value={hotel.manager_email} />
        <Info label="Billing email" value={hotel.billing_email} />
        <Info label="Address" value={hotel.hotel_address} />
        <Info label="Country" value={hotel.country} />
        <Info label="Tier" value={hotel.membership_tier_name} />
        <Info label="Monthly allocation" value={(hotel.monthly_credit_allocation || 0).toLocaleString()} />
        <Info label="Monthly used" value={(hotel.monthly_credits_used_current_period || 0).toLocaleString()} />
        <Info label="Monthly remaining" value={(hotel.monthly_credits_remaining || 0).toLocaleString()} />
        <Info label="Add-on balance" value={(hotel.addon_credits_remaining || 0).toLocaleString()} />
        <Info label="Total available" value={((hotel.monthly_credits_remaining || 0) + (hotel.addon_credits_remaining || 0)).toLocaleString()} highlight />
        <Info label="Total messages" value={(hotel.total_messages || 0).toLocaleString()} />
        <Info label="Purchase date" value={hotel.created_at ? new Date(hotel.created_at).toLocaleString() : "—"} />
        <Info label="Next renewal" value={hotel.next_renewal_at ? new Date(hotel.next_renewal_at).toLocaleString() : "—"} />
        <Info label="Code arrangement" value={hotel.code_arrangement === "shared" ? "One shared code" : "Separate venue codes"} />
        <Info label="Stripe customer" value={hotel.stripe_customer_id} mono />
        <Info label="Stripe subscription" value={hotel.stripe_subscription_id} mono />
        <Info label="Stripe checkout" value={hotel.stripe_checkout_session_id} mono />
      </div>

      <div className="flex flex-wrap gap-2">
        <button disabled={!!busy} onClick={editContact} data-testid={`lumi-hotel-edit-${hotel.hotel_id}`} className="text-xs px-3 py-1.5 rounded-full bg-neutral-900 text-white hover:bg-black disabled:opacity-50">Edit contact info</button>
        <button disabled={!!busy} onClick={editWelcomeName} data-testid={`lumi-hotel-edit-welcome-${hotel.hotel_id}`} className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-800 hover:bg-amber-200 disabled:opacity-50">Edit welcome name</button>
        <button disabled={!!busy} onClick={() => doAction("Resend email", `/hotels/${hotel.hotel_id}/resend-email`)} data-testid={`lumi-hotel-resend-${hotel.hotel_id}`} className="text-xs px-3 py-1.5 rounded-full bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50">Resend activation email</button>
        {hotel.status === "active" ? (
          <button disabled={!!busy} onClick={() => doAction("Deactivate", `/hotels/${hotel.hotel_id}/deactivate`)} data-testid={`lumi-hotel-deactivate-${hotel.hotel_id}`} className="text-xs px-3 py-1.5 rounded-full bg-rose-100 text-rose-800 hover:bg-rose-200 disabled:opacity-50">Pause hotel</button>
        ) : (
          <button disabled={!!busy} onClick={() => doAction("Reactivate", `/hotels/${hotel.hotel_id}/reactivate`)} data-testid={`lumi-hotel-reactivate-${hotel.hotel_id}`} className="text-xs px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-800 hover:bg-emerald-200 disabled:opacity-50">Reactivate hotel</button>
        )}
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-2">Activation codes &amp; devices</p>
        <div className="space-y-3">
          {codes.map((c) => {
            const codeDevices = (devices || []).filter((d) => d.code_id === c.code_id);
            const activeCount = codeDevices.filter((d) => !d.revoked).length;
            const expected = c.expected_devices || 1;
            const codeAudits = (audits || []).filter((a) => a.code_id === c.code_id);
            return (
              <div key={c.code_id} className="rounded-lg bg-white border border-neutral-200 overflow-hidden" data-testid={`lumi-hotel-code-group-${c.code_id}`}>
                <div className="flex items-center justify-between px-3 py-2 bg-neutral-50 border-b border-neutral-200 text-xs flex-wrap gap-2">
                  <div className="min-w-0 pr-2">
                    <p className="font-semibold truncate">{c.venue_label} — <span className="font-mono">{c.code}</span></p>
                    <p className="text-[10px] text-neutral-500 uppercase tracking-wide" data-testid={`lumi-hotel-code-cap-${c.code_id}`}>
                      {c.status} · <strong className="text-neutral-700">{activeCount}</strong> of <strong className="text-neutral-700">{expected}</strong> device{expected === 1 ? "" : "s"} activated
                    </p>
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0 flex-wrap">
                    <button onClick={() => copyCode(c.code)} className="px-2 py-1 rounded-full bg-neutral-100 hover:bg-neutral-200">Copy</button>
                    <button onClick={() => renameCode(c.code_id)} className="px-2 py-1 rounded-full bg-neutral-100 hover:bg-neutral-200">Rename</button>
                    <button onClick={() => editDeviceLimit(c)} data-testid={`lumi-hotel-edit-limit-${c.code_id}`} className="px-2 py-1 rounded-full bg-neutral-100 hover:bg-neutral-200">Device limit</button>
                    {c.status === "active" && (
                      <>
                        <button onClick={() => doAction("Revoke code", `/codes/${c.code_id}/revoke`)} className="px-2 py-1 rounded-full bg-rose-100 text-rose-800 hover:bg-rose-200">Revoke code</button>
                        <button onClick={() => doAction("Replace code", `/hotels/${hotel.hotel_id}/codes/replace/${c.code_id}`)} className="px-2 py-1 rounded-full bg-amber-100 text-amber-800 hover:bg-amber-200">Replace</button>
                      </>
                    )}
                  </div>
                </div>
                {codeAudits.length > 0 && (
                  <div className="px-3 py-2 bg-amber-50/60 border-b border-amber-100" data-testid={`lumi-hotel-code-history-${c.code_id}`}>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-800 mb-1">Device-limit history</p>
                    <ul className="space-y-0.5">
                      {codeAudits.map((a) => (
                        <li key={a.audit_id} className="text-[11px] text-neutral-700 flex flex-wrap gap-x-2">
                          <span className="font-mono">{a.old_limit} → <strong>{a.new_limit}</strong></span>
                          <span className="text-neutral-500">·</span>
                          <span>{a.at ? new Date(a.at).toLocaleString() : "—"}</span>
                          <span className="text-neutral-500">·</span>
                          <span className="truncate">{a.manager_email || "—"}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="divide-y divide-neutral-100">
                  {codeDevices.length === 0 && (
                    <p className="text-[11px] text-neutral-500 px-3 py-2">No devices have activated with this code yet.</p>
                  )}
                  {codeDevices.map((d) => (
                    <div key={d.device_token} className="flex items-center justify-between px-3 py-2 text-[11px]" data-testid={`lumi-hotel-device-${d.device_token}`}>
                      <div className="min-w-0 pr-2">
                        <p className="font-semibold truncate text-neutral-800">{d.device_hint || "(no name)"}</p>
                        <p className="text-[10px] text-neutral-500">
                          Activated {d.first_activated_at ? new Date(d.first_activated_at).toLocaleString() : "—"} · Last active {d.last_seen_at ? new Date(d.last_seen_at).toLocaleString() : "—"}
                        </p>
                        <p className="text-[10px] text-neutral-400 font-mono truncate">{d.device_token}</p>
                      </div>
                      {d.revoked ? (
                        <span className="text-[10px] uppercase text-neutral-400 flex-shrink-0">revoked</span>
                      ) : (
                        <button
                          onClick={() => doAction("Revoke device", `/devices/${d.device_token}/revoke`)}
                          data-testid={`lumi-hotel-revoke-device-${d.device_token}`}
                          className="px-2 py-1 rounded-full bg-rose-100 text-rose-800 hover:bg-rose-200 flex-shrink-0"
                        >
                          Revoke device
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        {(() => {
          // Any devices whose code_id no longer maps to a live code (e.g. code deleted). Show them so admins can still revoke.
          const orphaned = (devices || []).filter((d) => !codes.some((c) => c.code_id === d.code_id));
          if (orphaned.length === 0) return null;
          return (
            <div className="mt-3 rounded-lg border border-dashed border-neutral-300 bg-neutral-50 p-3">
              <p className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide mb-1">Orphaned devices ({orphaned.length})</p>
              {orphaned.map((d) => (
                <div key={d.device_token} className="flex items-center justify-between text-[11px] py-1">
                  <span className="truncate">{d.device_hint || d.device_token}</span>
                  {!d.revoked && (
                    <button onClick={() => doAction("Revoke device", `/devices/${d.device_token}/revoke`)} className="px-2 py-1 rounded-full bg-rose-100 text-rose-800 hover:bg-rose-200 flex-shrink-0">Revoke device</button>
                  )}
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-2">Payments</p>
          <div className="space-y-1.5">
            {payments.length === 0 && <p className="text-xs text-neutral-500">No payments recorded.</p>}
            {payments.map((p, i) => (
              <div key={i} className="text-xs bg-white border border-neutral-200 rounded-lg px-3 py-2">
                <p className="font-semibold">{p.type} · +{(p.credits_added || 0).toLocaleString()} credits</p>
                <p className="text-[10px] text-neutral-500">{p.at ? new Date(p.at).toLocaleString() : "—"}</p>
              </div>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-2">Recent credit ledger</p>
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {ledger.map((l, i) => (
              <div key={i} className="text-[11px] text-neutral-700 flex justify-between border-b border-dashed border-neutral-200 py-0.5">
                <span>{l.direction === "credit" ? "+" : "−"}{l.amount} · {l.source}</span>
                <span className="text-neutral-400">{new Date(l.created_at).toLocaleString()}</span>
              </div>
            ))}
            {ledger.length === 0 && <p className="text-xs text-neutral-500">No entries yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

function Info({ label, value, mono, highlight }) {
  return (
    <div className={`rounded-lg p-2 ${highlight ? "bg-amber-50 border border-amber-100" : "bg-white border border-neutral-200"}`}>
      <p className="text-[10px] uppercase tracking-wide text-neutral-500">{label}</p>
      <p className={`text-xs ${mono ? "font-mono" : ""} break-all`}>{value || "—"}</p>
    </div>
  );
}
